import {
  Button,
  Select,
  MenuItem,
  Box,
  Typography,
  Grid,
  Drawer,
} from "@mui/material";
import React, {useEffect, useState, useCallback} from "react";
import {
  getColumnSettings,
  ColumnSettings,
  deleteTemplate,
  saveTemplate,
  updateTemplate,
  getDefaultColumnSettings,
} from "@/services/Template/TemplateService";
import TextField from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import CloseIcon from "@mui/icons-material/Close";
import {ReactComponent as TemplateEditIcon} from "@/assets/template/Edit.svg";
import {ReactComponent as TemplateDeleteIcon} from "@/assets/template/Delete.svg";
import {ReactComponent as SaveAsIcon} from "@/assets/template/SaveAsNew.svg";
import {ReactComponent as ResetAltIcon} from "@/assets/template/Reset.svg";
import {ReactComponent as CheckCircleIcon} from "@/assets/template/SaveAndApply.svg";
import {ReactComponent as ColumnSettingIcon} from "@/assets/template/ColumnSetting.svg";
import Left from "./Left";
import Right from "./Right";
import ZkDialog from "@/components/dialog";
import {useTranslation} from "react-i18next";
import SnackbarUtil from "@/utils/SnackbarUtil";

type ColumnSettingsDialogProps = {
  report_type: any;
  onColumnsChange?: (columns: any[]) => void;
};

const ColumnSettingsDialog: React.FC<ColumnSettingsDialogProps> = ({
                                                                     report_type = 'timecard',
                                                                     onColumnsChange,
                                                                   }) => {
  const [tableColumns, setTableColumnsData] = React.useState<ColumnSettings[] | []>([]);
  const [currentColumn, setCurrentColumn] = React.useState<ColumnSettings | null>(null);
  const [originalTemplate, setOriginalTemplate] = useState<ColumnSettings | null>(null); // 保存当前模板原始数据
  const [open, setOpen] = React.useState(false);
  const [newName, setNewName] = useState("");
  const [nameDialog, setNameDialog] = useState(false);
  const [operationType, setOperationType] = useState("");
  const {t} = useTranslation();

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const isDefaultTemplate = !!currentColumn?.is_default;

  const getTableColumns = useCallback(async () => {
    const res = await getColumnSettings(report_type);
    let list: ColumnSettings[] = [];

    if (res.data?.data.length === 0) {
      const defaultRes = await getDefaultColumnSettings(report_type);
      list = defaultRes as ColumnSettings[];
    } else {
      list = res.data?.data as ColumnSettings[] || [];
    }

    setTableColumnsData(list);

    const activeColumn = list.find((item) => item.is_active) || list[0];
    if (activeColumn) {
      const templateFieldArray = JSON.parse(activeColumn.template_field as unknown as string);
      const columns = templateFieldArray.map((item: any) => ({
        label: item.label,
        key: item.key,
        isCheck: item.isCheck,
        required: item.required,
        sort: item.sort,
      }));
      const template = {...activeColumn, template_field: columns};
      setCurrentColumn(JSON.parse(JSON.stringify(template)));
      setOriginalTemplate(JSON.parse(JSON.stringify(template))); // 保存原始模板
    } else {
      setCurrentColumn(null);
      setOriginalTemplate(null);
    }

    setHasUnsavedChanges(false);
  }, [report_type]);

  useEffect(() => {
    if (open) {
      getTableColumns();
    }
  }, [open, getTableColumns]);

  const handleClose = () => setOpen(false);
  const handleOpen = () => setOpen(true);

  const handleChangeSelect = (value: string) => {
    if (hasUnsavedChanges && !currentColumn?.is_default) {
      ZkDialog.confirm({
        title: t("common.Unsaved Changes"),
        content: t("common.There are unsaved modifications in the current template.Do you want to save them before switching?"),
        onConfirm: async () => {
          if (currentColumn) {
            await updateTemplate(currentColumn.id, {
              template_field: currentColumn.template_field,
            });
          }
          setHasUnsavedChanges(false);
          await getTableColumns();
          switchToTemplate(value);
        },
        onClose: () => {
          // 取消，跳过
          switchToTemplate(value);
        },
      });
      return;
    }

    switchToTemplate(value);
  };

  // 切换到选中的模板，并保存原始数据
  const switchToTemplate = (value: string) => {
    const selectedColumn = tableColumns.find((item) => item.template_name === value);
    if (selectedColumn) {
      const templateFieldArray = JSON.parse(selectedColumn.template_field as unknown as string);
      const columns = templateFieldArray.map((item: any) => ({
        label: item.label,
        key: item.key,
        isCheck: item.isCheck,
        required: item.required,
        sort: item.sort,
      }));
      const template = {...selectedColumn, template_field: columns};
      setCurrentColumn(JSON.parse(JSON.stringify(template)));
      setOriginalTemplate(JSON.parse(JSON.stringify(template))); // 保存选中模板的原始数据
      setHasUnsavedChanges(false);
    }
  };

  const btnSx = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0px 10px",
    textTransform: "capitalize",
    height: "42px",
  };

  const SaveAndApply = async () => {
    if (!currentColumn) return;
    const selectedColumns = currentColumn.template_field?.filter((col: any) => col.isCheck) || [];
    if (selectedColumns.length === 0) {
      SnackbarUtil.error(t("common.At least one column must be selected before saving."), {
        anchorOrigin: {vertical: 'top', horizontal: 'center'}
      });
      return;
    }
    setOpen(false);

    setTimeout(async () => {
      await updateTemplate(currentColumn.id, {
        template_field: currentColumn.template_field,
        is_active: 1,
        is_default: currentColumn.is_default,
      });
      if (!currentColumn.is_default) {
        SnackbarUtil.success(t("common.Configuration saved. Report updated with selected fields."), {
          anchorOrigin: {vertical: 'top', horizontal: 'center'}
        });
      } else {
        SnackbarUtil.success(t("common.Default template save and apply tips1"), {
          anchorOrigin: {vertical: 'top', horizontal: 'center'}
        });
      }
      
      await getTableColumns();
      setHasUnsavedChanges(false);

      if (onColumnsChange) {
        onColumnsChange(currentColumn.template_field || []);
      }
    }, 100);
  };

  const deleteFn = () => {
    if (isDefaultTemplate) {
      return;
    }
    ZkDialog.confirm({
      title: t("common.Delete Template"),
      content: t("common.You sure to delete?", {value: currentColumn?.template_name}),
      onConfirm: async () => {
        if (!currentColumn) return;
        if (currentColumn.is_default) {
          SnackbarUtil.error(t("common.Default Template can not be deleted."), {
            anchorOrigin: {vertical: 'top', horizontal: 'center'}
          });
          return;
        }
        await deleteTemplate(currentColumn.id);
        SnackbarUtil.success(t("common.Template deleted successfully."), {
          anchorOrigin: {vertical: 'top', horizontal: 'center'}
        });
        await getTableColumns();
      },
    });
  };

  const handleCloseNameDialog = () => {
    setNameDialog(false);
    setNewName("");
  };

  const handleOpenNameDialog = (type: string) => {
    if (isDefaultTemplate && type !== "saveNew") {
      return;
    }
    setNewName(type === "modify" ? currentColumn?.template_name || "" : "");
    setOperationType(type);
    setNameDialog(true);
  };

  const handleUpdateTemplate = async () => {
    if (!currentColumn || !newName) return;
    const selectedColumns = currentColumn.template_field?.filter((col: any) => col.isCheck) || [];
    if (selectedColumns.length === 0) {
      SnackbarUtil.error(t("common.At least one column must be selected before saving."), {
        anchorOrigin: {vertical: 'top', horizontal: 'center'}
      });
      return;
    }
    if (operationType === 'modify') {
      const existingTemplate = tableColumns.find(
        template => template.template_name === newName && template.id !== currentColumn.id
      );

      if (existingTemplate) {
        SnackbarUtil.error(t("common.The template name already exists."), {
          anchorOrigin: {vertical: 'top', horizontal: 'center'}
        });
        return;
      }
      await updateTemplate(currentColumn.id, {template_name: newName});
      SnackbarUtil.success(t("common.Template updated successfully."), {
        anchorOrigin: {vertical: 'top', horizontal: 'center'}
      });
    } else {
      if (tableColumns.length >= 5) {
        SnackbarUtil.error(t("common.Just can only save up to 5 templates."), {
          anchorOrigin: {vertical: 'top', horizontal: 'center'}
        });
        return;
      }
      if (tableColumns.find(template => template.template_name === newName)) {
        SnackbarUtil.error(t("common.The template name already exists."), {
          anchorOrigin: {vertical: 'top', horizontal: 'center'}
        });
        return;
      }
      await saveTemplate({
        template_name: newName,
        template_field: currentColumn.template_field,
        is_default: 0,
        report_type: report_type === 'timecard' ? 1 : 2,
      }).then(res => {
        if (res.status < 400) {
          SnackbarUtil.success(t("common.Template added successfully."), {
            anchorOrigin: {vertical: 'top', horizontal: 'center'}
          });
        } else {
          SnackbarUtil.error(res?.message, {
            anchorOrigin: {vertical: 'top', horizontal: 'center'}
          });
        }
      });
    }
    await getTableColumns();
    setNameDialog(false);
    setNewName("");
  };

  // ✅ 修复：Reset 只重置当前选中模板，不重新拉取接口
  const handleReset = () => {
    if (!originalTemplate) return;
    setCurrentColumn(JSON.parse(JSON.stringify(originalTemplate)));
    setHasUnsavedChanges(false);
  };

  return (
    <React.Fragment>
      <Button
        variant="text"
        sx={{textTransform: "capitalize", color: "#75828a", fontWeight: 400}}
        onClick={handleOpen}
      >
        <ColumnSettingIcon/>
        <Typography sx={{marginLeft: "5px", fontSize: "14px", color: "#75828A"}}>
          {t('common.Column Settings')}
        </Typography>
      </Button>
      <Drawer
        sx={{
          ".MuiPaper-root": {
            height: "80vh",
            marginTop: "10vh",
            marginRight: "25px",
            borderRadius: "4px",
            background: "#F6F6FA",
            width: "50vw",
            minWidth: "400px",
          },
        }}
        anchor="right"
        open={open}
        onClose={handleClose}
      >
        <Box sx={{display: "flex", flexDirection: "column", height: "100%"}}>
          <Grid sx={{
            height: "50px",
            padding: "10px 10px",
            borderBottom: "1px solid #afaaaa",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}>
            <Typography sx={{fontSize: "16px", fontWeight: "bold"}}>
              {t('common.Column Settings')}
            </Typography>
            <CloseIcon sx={{cursor: "pointer"}} onClick={handleClose}/>
          </Grid>

          <Grid sx={{
            padding: "10px 20px",
            display: "flex",
            alignItems: "center",
            borderBottom: "1px solid #afaaaa",
          }}>
            <Grid sx={{
              display: "flex",
              alignItems: "center",
              border: "1px solid #afa7a7",
              borderRadius: "4px",
              padding: "0px 10px",
              height: "42px",
            }}>
              <Select
                size="small"
                sx={{
                  width: "240px",
                  border: "none",
                  padding: "0px 5px",
                  "& .MuiOutlinedInput-notchedOutline": {border: "none"},
                }}
                value={currentColumn?.template_name || ''}
                onChange={(e) => handleChangeSelect(e.target.value)}
              >
                {tableColumns?.map((item) => (
                  <MenuItem key={item.id} value={item.template_name}>
                    {item.template_name}
                  </MenuItem>
                ))}
              </Select>

              <Grid
                onClick={() => handleOpenNameDialog("modify")}
                sx={{
                  cursor: isDefaultTemplate ? "not-allowed" : "pointer",
                  opacity: isDefaultTemplate ? 0.4 : 1,
                }}
              >
                <TemplateEditIcon/>
              </Grid>
              <Grid
                onClick={deleteFn}
                sx={{
                  cursor: isDefaultTemplate ? "not-allowed" : "pointer",
                  opacity: isDefaultTemplate ? 0.4 : 1,
                }}
              >
                <TemplateDeleteIcon/>
              </Grid>
            </Grid>

            <Button onClick={() => handleOpenNameDialog("saveNew")} variant="outlined" sx={btnSx}>
              <SaveAsIcon/>
              <Typography variant="body2" sx={{marginLeft: "5px"}}>
                {t("common.Save as New Template")}
              </Typography>
            </Button>

            <Button variant="outlined" onClick={handleReset} sx={btnSx}>
              <ResetAltIcon/>
              <Typography variant="body2" sx={{marginLeft: "5px"}}>
                {t("common.Reset")}
              </Typography>
            </Button>

            <Button onClick={SaveAndApply} variant="contained" sx={btnSx}>
              <CheckCircleIcon/>
              <Typography variant="body2" sx={{marginLeft: "5px"}}>
                {t("common.Save & Apply")}
              </Typography>
            </Button>
          </Grid>

          <Grid sx={{flex: 1, overflow: "auto"}}>
            <Grid sx={{display: "flex", alignItems: "stretch", minHeight: "60%"}}>
              <Grid sx={{width: "60%"}}>
                <Left
                  columnSettings={currentColumn}
                  updateColumnSettings={(val) => {
                    setCurrentColumn(val);
                    setHasUnsavedChanges(true);
                  }}
                />
              </Grid>
              <Grid sx={{width: "40%", borderLeft: "1px solid #afaaaa"}}>
                <Right
                  columnSettings={currentColumn}
                  updateColumnSettings={(val) => {
                    setCurrentColumn(val);
                    setHasUnsavedChanges(true);
                  }}
                />
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </Drawer>

      <Dialog open={nameDialog} onClose={handleCloseNameDialog} PaperProps={{
        component: "form",
        onSubmit: (event: React.FormEvent<HTMLFormElement>) => {
          event.preventDefault();
          if (newName === "") {
            SnackbarUtil.error(t("common.Please enter template name"), {
              anchorOrigin: {vertical: 'top', horizontal: 'center'}
            });
            return;
          } else if (newName.length > 20) {
            SnackbarUtil.error(t("common.The length of this field cannot exceed digits", {
              field_name: t("common.Template Name"),
              number: 20
            }), {
              anchorOrigin: {vertical: 'top', horizontal: 'center'}
            });
            return;
          }
          handleUpdateTemplate();
        },
      }}>
        <DialogTitle sx={{color: "#999999", fontWeight: "normal"}}>
          {operationType === 'modify' ? t("common.Edit Template Name") : t("common.Save as New Template")}
        </DialogTitle>
        <DialogContent sx={{minWidth: "400px"}}>
          <TextField
            autoFocus
            required
            margin="dense"
            id="name"
            name="name"
            onChange={(event) => setNewName(event.target.value)}
            label=""
            placeholder={t("common.Enter template name")}
            value={newName}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={handleCloseNameDialog}>
            {t("common.cancel")}
          </Button>
          <Button variant="contained" type="submit">
            {t("common.save")}
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
};

export default ColumnSettingsDialog;
